/**
 * The four stages a user sees while a track is built, derived from the
 * track's status and the pipeline steps its latest job has completed. The
 * workflow has six pipeline steps; the user never sees those names.
 */

export type StageState = "Done" | "Working" | "Waiting" | "Failed" | "Skipped";

export type Stage = { key: StageKey; label: string; state: StageState };

type StageKey = "received" | "separated" | "lyrics" | "mastered";

/** Each stage is done once its workflow step has reported success. Mastering
 * is the tail of the workflow and is done when the track itself is ready. */
const STAGES: ReadonlyArray<{ key: StageKey; label: string; doneAfterStep: string | null; percent: number }> = [
  { key: "received", label: "Audio received", doneAfterStep: null, percent: 8 },
  { key: "separated", label: "Vocal separated", doneAfterStep: "vocal-separation", percent: 45 },
  { key: "lyrics", label: "Lyrics timed", doneAfterStep: "lyrics-alignment", percent: 80 },
  { key: "mastered", label: "Track mastered", doneAfterStep: null, percent: 100 },
];

interface StageInput {
  processingStatus: string;
  /** Steps the latest job has reported as successful. */
  completedSteps: ReadonlyArray<string>;
}

/** Stage states plus a percent for the progress bar. Every stage after the
 * first unfinished one is Waiting while the pipeline runs, or Skipped once it
 * has failed. */
export function pipelineStages({ processingStatus, completedSteps }: StageInput): {
  stages: Stage[];
  percent: number;
} {
  if (processingStatus === "ready") {
    return { stages: STAGES.map((s) => ({ key: s.key, label: s.label, state: "Done" })), percent: 100 };
  }
  const done = new Set(completedSteps);
  const isDone = (stage: (typeof STAGES)[number]) =>
    stage.doneAfterStep === null ? stage.key === "received" : done.has(stage.doneAfterStep);

  let percent = 0;
  let reachedOpen = false;
  const stages = STAGES.map((stage) => {
    if (!reachedOpen && isDone(stage)) {
      percent = stage.percent;
      return { key: stage.key, label: stage.label, state: "Done" as const };
    }
    const first = !reachedOpen;
    reachedOpen = true;
    if (processingStatus === "failed") {
      return { key: stage.key, label: stage.label, state: first ? ("Failed" as const) : ("Skipped" as const) };
    }
    const active = first && processingStatus === "processing";
    return { key: stage.key, label: stage.label, state: active ? ("Working" as const) : ("Waiting" as const) };
  });
  return { stages, percent };
}
